import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  Circle,
  Plus,
  Trash2,
  Mail,
  RefreshCw,
  BookOpen,
} from 'lucide-react';
import {
  getUserSenderIdentities,
  createSenderIdentity,
  checkSenderVerification,
  resendSenderVerification,
  initiateSenderVerification,
  setDefaultSender,
  deleteSenderIdentity,
  type UserSenderIdentity,
} from '@/services/userSenderIdentitiesService';

interface UserSenderIdentitiesProps {
  userId: string;
  companyId: string;
}

export const UserSenderIdentities = ({ userId, companyId }: UserSenderIdentitiesProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [senders, setSenders] = useState<UserSenderIdentity[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [adding, setAdding] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [senderToDelete, setSenderToDelete] = useState<UserSenderIdentity | null>(null);

  // Form state for new sender
  const [newSender, setNewSender] = useState({
    nickname: '',
    from_email: '',
    from_name: '',
    reply_to_email: '',
  });

  useEffect(() => {
    loadSenders();
  }, [userId]);

  const loadSenders = async () => {
    try {
      setLoading(true);
      const data = await getUserSenderIdentities(userId);
      setSenders(data);
    } catch (error: any) {
      console.error('Error loading senders:', error);
      toast({
        title: 'Error',
        description: 'Failed to load sender identities',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSender = async () => {
    try {
      // Validation
      if (!newSender.nickname || !newSender.from_email || !newSender.from_name) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          variant: 'destructive',
        });
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newSender.from_email)) {
        toast({
          title: 'Validation Error',
          description: 'Please enter a valid email address',
          variant: 'destructive',
        });
        return;
      }

      setAdding(true);
      const result = await createSenderIdentity(newSender);

      if (result.success) {
        toast({
          title: 'Verification Email Sent',
          description: `A verification email has been sent to ${newSender.from_email}. Please check your inbox and click the verification link.`,
        });

        // Reset form and close dialog
        setNewSender({
          nickname: '',
          from_email: '',
          from_name: '',
          reply_to_email: '',
        });
        setShowAddDialog(false);

        // Reload senders
        await loadSenders();
      } else {
        throw new Error(result.error || 'Failed to create sender identity');
      }
    } catch (error: any) {
      console.error('Error adding sender:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add sender identity',
        variant: 'destructive',
      });
    } finally {
      setAdding(false);
    }
  };

  const handleCheckStatus = async (senderId: string) => {
    try {
      setCheckingStatus(senderId);
      const result = await checkSenderVerification(senderId);

      if (result.verified) {
        toast({
          title: 'Sender Verified!',
          description: 'Your sender email is now verified. You can set it as default.',
        });
      } else {
        toast({
          title: 'Still Pending',
          description: result.message || 'Verification is still pending. Please check your email.',
        });
      }

      // Reload to get updated status
      await loadSenders();
    } catch (error: any) {
      console.error('Error checking status:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to check verification status',
        variant: 'destructive',
      });
    } finally {
      setCheckingStatus(null);
    }
  };

  const handleResendVerification = async (senderId: string) => {
    try {
      const result = await resendSenderVerification(senderId);

      if (result.success) {
        toast({
          title: 'Verification Email Resent',
          description: result.message || 'A new verification email has been sent.',
        });
      }
    } catch (error: any) {
      console.error('Error resending verification:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to resend verification email',
        variant: 'destructive',
      });
    }
  };

  const handleStartVerification = async (sender: UserSenderIdentity) => {
    try {
      const result = await initiateSenderVerification(sender);
      if (result.success) {
        toast({
          title: 'Verification Email Sent',
          description: `A verification email has been sent to ${sender.from_email}. Please check your inbox.`,
        });
        await loadSenders();
      }
    } catch (error: any) {
      console.error('Error initiating verification:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to start verification',
        variant: 'destructive',
      });
    }
  };

  const handleSetDefault = async (senderId: string) => {
    try {
      await setDefaultSender(senderId, userId);
      toast({
        title: 'Default Sender Updated',
        description: 'This sender is now your default for all emails.',
      });
      await loadSenders();
    } catch (error: any) {
      console.error('Error setting default:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to set default sender',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteClick = (sender: UserSenderIdentity) => {
    setSenderToDelete(sender);
  };

  const handleDeleteConfirm = async () => {
    if (!senderToDelete) return;

    try {
      setDeletingId(senderToDelete.id);
      const result = await deleteSenderIdentity(senderToDelete.id);

      if (result.success) {
        toast({
          title: 'Sender Deleted',
          description: 'The sender identity has been removed.',
        });
        await loadSenders();
      }
    } catch (error: any) {
      console.error('Error deleting sender:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete sender identity',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
      setSenderToDelete(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'pending':
        return <Loader2 className="h-5 w-5 text-yellow-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Circle className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'pending':
        return 'Pending Verification';
      case 'failed':
        return 'Verification Failed';
      default:
        return 'Not Verified';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="shadow-md hover:shadow-lg transition-shadow">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-xl">Sender Email Addresses</CardTitle>
              <CardDescription className="text-sm">
                Manage multiple verified sender identities for your emails. Only one can be active at a time.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                asChild
              >
                <a 
                  href="/documentation#sendgrid-setup" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1"
                >
                  <BookOpen className="h-4 w-4" />
                  Setup Guide
                </a>
              </Button>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Sender
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {senders.length === 0 ? (
            <div className="text-center py-16">
              <Mail className="h-16 w-16 mx-auto text-muted-foreground/50 mb-6" />
              <h3 className="text-lg font-semibold mb-2">No Sender Identities</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                Add your first sender email to start sending emails via SendGrid
              </p>
              <Button onClick={() => setShowAddDialog(true)} className="shadow-md hover:shadow-lg transition-shadow">
                <Plus className="mr-2 h-4 w-4" />
                Add First Sender
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {senders.map((sender) => (
                <div
                  key={sender.id}
                  className="flex items-center justify-between p-5 border rounded-lg hover:bg-muted/30 hover:shadow-md transition-all bg-card"
                >
                  <div className="flex items-center gap-3 flex-1">
                    {getStatusIcon(sender.verification_status)}

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{sender.from_email}</p>
                        {sender.is_default && (
                          <Badge variant="secondary">Default</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {sender.from_name} • {sender.nickname}
                      </p>
                      {sender.verification_status === 'pending' && (
                        <p className="text-xs text-yellow-700 mt-1">
                          Verification pending - check your email inbox
                        </p>
                      )}
                      {sender.verification_error && (
                        <p className="text-xs text-red-700 mt-1">
                          {sender.verification_error}
                        </p>
                      )}
                      {sender.verification_status === 'verified' && sender.verified_at && (
                        <p className="text-xs text-green-700 mt-1">
                          Verified on {new Date(sender.verified_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {sender.verification_status === 'verified' && !sender.is_default && (
                      <Button
                        size="sm"
                        onClick={() => handleSetDefault(sender.id)}
                        className="shadow-sm hover:shadow-md transition-shadow"
                      >
                        Set as Default
                      </Button>
                    )}

                    {sender.verification_status === 'not_started' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStartVerification(sender)}
                      >
                        Start Verification
                      </Button>
                    )}

                    {sender.verification_status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCheckStatus(sender.id)}
                          disabled={checkingStatus === sender.id}
                        >
                          {checkingStatus === sender.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <RefreshCw className="mr-2 h-4 w-4" />
                              Check Status
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResendVerification(sender.id)}
                        >
                          Resend Email
                        </Button>
                      </>
                    )}

                    {sender.verification_status === 'failed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResendVerification(sender.id)}
                      >
                        Resend Email
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteClick(sender)}
                      disabled={deletingId === sender.id}
                    >
                      {deletingId === sender.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Sender Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Sender Identity</DialogTitle>
            <DialogDescription>
              Create a new verified sender email address. SendGrid will send a verification email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="nickname">Nickname *</Label>
              <Input
                id="nickname"
                value={newSender.nickname}
                onChange={(e) => setNewSender({ ...newSender, nickname: e.target.value })}
                placeholder="Work Email"
              />
              <p className="text-xs text-muted-foreground mt-1">
                A friendly name to identify this sender
              </p>
            </div>

            <div>
              <Label htmlFor="from-email">From Email *</Label>
              <Input
                id="from-email"
                type="email"
                value={newSender.from_email}
                onChange={(e) => setNewSender({ ...newSender, from_email: e.target.value })}
                placeholder="you@company.com"
              />
            </div>

            <div>
              <Label htmlFor="from-name">From Name *</Label>
              <Input
                id="from-name"
                value={newSender.from_name}
                onChange={(e) => setNewSender({ ...newSender, from_name: e.target.value })}
                placeholder="John Doe"
              />
            </div>

            <div>
              <Label htmlFor="reply-to">Reply-To Email (Optional)</Label>
              <Input
                id="reply-to"
                type="email"
                value={newSender.reply_to_email}
                onChange={(e) => setNewSender({ ...newSender, reply_to_email: e.target.value })}
                placeholder="reply@company.com"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSender} disabled={adding}>
              {adding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Sender
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!senderToDelete}
        onOpenChange={(open) => !open && setSenderToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sender Identity?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>{senderToDelete?.from_email}</strong>? This will remove it from both
              SendGrid and your account.
              {senderToDelete?.is_default && (
                <span className="block mt-2 text-yellow-600">
                  This is your default sender. You'll need to set a new default after deleting.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
